import {
  CloudRain,
  LocateFixed,
  Search,
  SlidersHorizontal,
  Ticket,
  Timer,
} from "lucide-react";
import type { CSSProperties } from "react";
import { categories } from "../data/categories";
import { regionById } from "../data/regions";
import type { AreaGroupId, District, FilterState, RegionId } from "../data/schema";
import {
  areaGroupLabels,
  priceLabels,
  regionLabels,
  stayTimeLabels,
  stayTimes,
  weekdayLabels,
  weekdays,
} from "../lib/labels";
import { toggleArrayValue } from "../lib/filtering";

interface FilterPanelProps {
  filters: FilterState;
  areaGroups: AreaGroupId[];
  regions: RegionId[];
  districts: District[];
  resultCount: number;
  onChange: (filters: FilterState) => void;
  onReset: () => void;
}

export function FilterPanel({
  filters,
  areaGroups,
  regions,
  districts,
  resultCount,
  onChange,
  onReset,
}: FilterPanelProps) {
  return (
    <aside className="filter-panel" aria-label="장소 필터">
      <div className="panel-title">
        <SlidersHorizontal size={18} aria-hidden="true" />
        <strong>상세 조건</strong>
        <span>{resultCount}곳</span>
      </div>

      <label className="search-box">
        <Search size={18} aria-hidden="true" />
        <input
          value={filters.query}
          onChange={(event) => onChange({ ...filters, query: event.target.value })}
          aria-label="장소, 동네, 태그 검색"
          placeholder="장소, 동네, 태그 검색"
        />
      </label>

      <section className="filter-group">
        <div className="filter-heading">권역</div>
        <div className="segmented region">
          {areaGroups.map((areaGroup) => (
            <button
              key={areaGroup}
              className={filters.areaGroups.includes(areaGroup) ? "active" : ""}
              onClick={() =>
                onChange({
                  ...filters,
                  areaGroups: toggleArrayValue(filters.areaGroups, areaGroup),
                  regions: [],
                  districts: [],
                })
              }
              type="button"
            >
              {areaGroupLabels[areaGroup]}
            </button>
          ))}
        </div>
      </section>

      <section className="filter-group">
        <div className="filter-heading">시·도</div>
        <div className="segmented region">
          {regions.map((region) => (
            <button
              key={region}
              hidden={
                filters.areaGroups.length > 0 &&
                !filters.areaGroups.includes(regionById[region].areaGroup)
              }
              className={filters.regions.includes(region) ? "active" : ""}
              onClick={() =>
                onChange({
                  ...filters,
                  regions: toggleArrayValue(filters.regions, region),
                  districts: [],
                })
              }
              type="button"
            >
              {regionLabels[region]}
            </button>
          ))}
        </div>
      </section>

      <section className="filter-group">
        <div className="filter-heading">관심 활동</div>
        <div className="chip-grid">
          {categories.map((category) => (
            <button
              key={category.id}
              className={filters.categories.includes(category.id) ? "chip active" : "chip"}
              onClick={() =>
                onChange({
                  ...filters,
                  categories: toggleArrayValue(filters.categories, category.id),
                })
              }
              style={{ "--chip-color": category.color } as CSSProperties}
              type="button"
            >
              {category.shortName}
            </button>
          ))}
        </div>
      </section>

      <section className="filter-group">
        <div className="filter-heading">요일</div>
        <div className="segmented seven">
          {weekdays.map((weekday) => (
            <button
              key={weekday}
              className={filters.weekdays.includes(weekday) ? "active" : ""}
              onClick={() =>
                onChange({
                  ...filters,
                  weekdays: toggleArrayValue(filters.weekdays, weekday),
                })
              }
              type="button"
            >
              {weekdayLabels[weekday]}
            </button>
          ))}
        </div>
      </section>

      <section className="filter-group two-col">
        <div>
          <div className="filter-heading with-icon">
            <LocateFixed size={15} aria-hidden="true" />
            공간
          </div>
          <div className="segmented vertical">
            {[
              ["all", "전체"],
              ["indoor", "실내"],
              ["outdoor", "야외"],
              ["mixed", "실내외"],
            ].map(([value, label]) => (
              <button
                key={value}
                className={filters.indoorOutdoor === value ? "active" : ""}
                onClick={() =>
                  onChange({
                    ...filters,
                    indoorOutdoor: value as FilterState["indoorOutdoor"],
                  })
                }
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="filter-heading with-icon">
            <Ticket size={15} aria-hidden="true" />
            비용
          </div>
          <div className="segmented vertical">
            {[
              ["all", "전체"],
              ["free", priceLabels.free],
              ["paid", priceLabels.paid],
              ["mixed", priceLabels.mixed],
            ].map(([value, label]) => (
              <button
                key={value}
                className={filters.priceType === value ? "active" : ""}
                onClick={() =>
                  onChange({
                    ...filters,
                    priceType: value as FilterState["priceType"],
                  })
                }
                type="button"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="filter-group">
        <button
          className={filters.rainFriendlyOnly ? "toggle active" : "toggle"}
          onClick={() =>
            onChange({ ...filters, rainFriendlyOnly: !filters.rainFriendlyOnly })
          }
          type="button"
        >
          <CloudRain size={17} aria-hidden="true" />
          비 오는 날
        </button>
      </section>

      <section className="filter-group">
        <div className="filter-heading with-icon">
          <Timer size={15} aria-hidden="true" />
          이용 시간
        </div>
        <div className="chip-grid compact">
          {stayTimes.map((stayTime) => (
            <button
              key={stayTime}
              className={filters.stayTimes.includes(stayTime) ? "chip active" : "chip"}
              onClick={() =>
                onChange({
                  ...filters,
                  stayTimes: toggleArrayValue(filters.stayTimes, stayTime),
                })
              }
              type="button"
            >
              {stayTimeLabels[stayTime]}
            </button>
          ))}
        </div>
      </section>

      <section className="filter-group">
        <div className="filter-heading">세부 지역</div>
        <div className="district-list">
          {districts.map((district) => (
            <button
              key={district}
              className={filters.districts.includes(district) ? "active" : ""}
              onClick={() =>
                onChange({
                  ...filters,
                  districts: toggleArrayValue(filters.districts, district),
                })
              }
              type="button"
            >
              {district}
            </button>
          ))}
        </div>
      </section>

      <button className="reset-button" onClick={onReset} type="button">
        필터 초기화
      </button>
    </aside>
  );
}
